import { useEffect, useRef } from 'react';
import { useMutation } from '@tanstack/react-query';
import { updatePreferences } from '../../features/auth/api';
import { useCurrentUser } from '../../features/auth/useCurrentUser';
import { useLocaleStore } from './localeStore';
import { reconcileLocale } from './reconcileLocale';

/**
 * Keeps the signed-in user's language choice on their account rather than only
 * in this browser's localStorage, so it follows them to another device.
 *
 * Both directions live here, in one effect, so the language picker itself stays
 * unaware of sessions and keeps working on the public pages:
 *   - down: on the first /me for a user, their saved language wins;
 *   - up:   any later change in the UI is written to the account.
 *
 * localStorage is still written by the locale store — it's what anonymous
 * visitors get, and what avoids a flash of the wrong language before /me
 * resolves on the next visit.
 *
 * Call once inside the authenticated shell.
 */
export function useLocalePreferenceSync(): void {
  const { data: user } = useCurrentUser();
  const { locale, setLocale } = useLocaleStore();

  /** The user whose account we've already reconciled against. */
  const reconciledFor = useRef<string | null>(null);
  /** What we believe the account currently holds. */
  const serverHolds = useRef<string | null>(null);

  const { mutate: save } = useMutation({
    mutationFn: (next: string) => updatePreferences(next),
    onError: () => {
      // Let the next change retry. The UI already switched language, so this
      // failing only means the account didn't record it — not worth a toast.
      serverHolds.current = null;
    },
  });

  useEffect(() => {
    if (!user) {
      // Signed out: forget everything so the next user reconciles cleanly.
      reconciledFor.current = null;
      serverHolds.current = null;
      return;
    }

    const firstPass = reconciledFor.current !== user.id;
    const action = reconcileLocale({
      saved: firstPass ? user.preferredLocale : serverHolds.current,
      local: locale,
      reconciled: !firstPass,
    });

    if (firstPass) reconciledFor.current = user.id;
    if (action.serverNowHolds !== undefined) serverHolds.current = action.serverNowHolds;
    if (action.apply) setLocale(action.apply);
    if (action.save) save(action.save);
  }, [user, locale, setLocale, save]);
}
